import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Truck, Shield, Clock, MapPin, ArrowRight, Star, CheckCircle2, Search, Calendar as CalendarIcon, Check, Zap, Crown, Building2 } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  
  // Search state
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      const uniqueCities = [...new Set(res.data.map(c => c.city).filter(Boolean))].sort();
      setCities(uniqueCities);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedCity) params.set('city', selectedCity);
    if (startDate) params.set('start', startDate.toISOString());
    if (endDate) params.set('end', endDate.toISOString());
    
    navigate(`/catalogo?${params.toString()}`);
  };

  const features = [
    {
      icon: Truck,
      title: "Ampia Flotta",
      description: "Centinaia di furgoni disponibili in tutta Italia, da piccoli van a grandi mezzi commerciali."
    },
    {
      icon: Shield,
      title: "Sicurezza Garantita",
      description: "Tutti i veicoli sono assicurati e sottoposti a regolare manutenzione."
    },
    {
      icon: Clock,
      title: "Noleggio Flessibile",
      description: "Tariffe orarie, giornaliere e settimanali per adattarsi alle tue esigenze."
    },
    {
      icon: MapPin,
      title: "Ritiro Comodo",
      description: "Punti di ritiro e consegna in tutta Italia, vicino a te."
    }
  ];

  const stats = [
    { value: "500+", label: "Veicoli Disponibili" },
    { value: "50+", label: "Aziende Partner" },
    { value: "10K+", label: "Clienti Soddisfatti" },
    { value: "24/7", label: "Assistenza" }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={null}
        description="Noleggio furgoni in tutta Italia. Confronta prezzi da oltre 100 aziende, leggi recensioni e prenota online il furgone perfetto per traslochi, trasporti e viaggi."
        keywords="noleggio furgoni, affitto furgoni, noleggio furgoni economici, furgoni per trasloco, noleggio veicoli commerciali"
        url="/"
      />
      <Header />

      {/* Hero Section with Search */}
      <section className="relative min-h-[85vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1701478008234-784cac616332?crop=entropy&cs=srgb&fm=jpg&q=85')"
          }}
        >
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Noleggia il Furgone<br />
              <span className="text-primary">Perfetto per Te</span>
            </h1>
            <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
              Trova, confronta e prenota il furgone ideale in pochi click. 
              Oltre 500 veicoli disponibili in tutta Italia.
            </p>

            {/* Search Box */}
            <div className="bg-white p-4 md:p-6 shadow-2xl max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* City Selection */}
                <div className="md:col-span-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block text-left">
                    Dove
                  </label>
                  <Select value={selectedCity || "all"} onValueChange={(v) => setSelectedCity(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-12 border-gray-200" data-testid="home-city-select">
                      <MapPin className="h-4 w-4 mr-2 text-primary" />
                      <SelectValue placeholder="Scegli città" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le città</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date */}
                <div className="md:col-span-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block text-left">
                    Data Ritiro
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full h-12 justify-start border-gray-200 font-normal"
                        data-testid="home-start-date"
                      >
                        <CalendarIcon className="h-4 w-4 mr-2 text-primary" />
                        {startDate ? format(startDate, "dd MMM", { locale: it }) : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        disabled={(date) => date < new Date()}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* End Date */}
                <div className="md:col-span-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block text-left">
                    Data Riconsegna
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full h-12 justify-start border-gray-200 font-normal"
                        data-testid="home-end-date"
                      >
                        <CalendarIcon className="h-4 w-4 mr-2 text-primary" />
                        {endDate ? format(endDate, "dd MMM", { locale: it }) : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => date < (startDate || new Date())}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Search Button */}
                <div className="md:col-span-1">
                  <label className="text-xs font-bold text-transparent uppercase tracking-wider mb-1 block">
                    &nbsp;
                  </label>
                  <Button 
                    onClick={handleSearch}
                    className="w-full h-12 btn-primary"
                    data-testid="home-search-btn"
                  >
                    <Search className="h-5 w-5 mr-2" />
                    Cerca
                  </Button>
                </div>
              </div>

              {/* Quick Links */}
              <div className="flex flex-wrap justify-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-500">Ricerche popolari:</span>
                {["Milano", "Roma", "Torino", "Napoli"].map((city) => (
                  <button
                    key={city}
                    onClick={() => { setSelectedCity(city); handleSearch(); }}
                    className="text-xs text-primary hover:underline"
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-primary py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {stat.value}
                </p>
                <p className="text-sm text-white/80 uppercase tracking-wider mt-1">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Come <span className="text-primary">Funziona</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Noleggiare un furgone non è mai stato così semplice
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "1", title: "Cerca", desc: "Inserisci la tua città e le date di noleggio" },
              { step: "2", title: "Confronta", desc: "Sfoglia i veicoli disponibili e confronta i prezzi" },
              { step: "3", title: "Prenota", desc: "Prenota online in pochi click e ritira il furgone" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary text-primary-foreground text-2xl font-black flex items-center justify-center mx-auto mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {item.step}
                </div>
                <h3 className="font-bold text-xl mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Perché Scegliere <span className="text-primary">NoleggioFurgoni.it</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Una piattaforma completa per trovare il furgone perfetto, con la massima trasparenza e convenienza.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-dashboard hover:border-primary/50 transition-colors group"
              >
                <div className="bg-primary/10 w-12 h-12 flex items-center justify-center mb-4 group-hover:bg-primary transition-colors">
                  <feature.icon className="h-6 w-6 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section for Companies */}
      <section className="py-20 bg-foreground text-background">
        <div className="container mx-auto px-4">
          <div className="tetris-grid items-center">
            <div className="md:col-span-7">
              <span className="text-primary uppercase tracking-widest text-sm font-bold">Per le aziende</span>
              <h2 
                className="text-3xl md:text-4xl font-bold uppercase tracking-tight mt-2 mb-4"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Hai una Flotta di Furgoni?<br />
                <span className="text-primary">Unisciti a Noi</span>
              </h2>
              <p className="text-muted-foreground mb-6 max-w-lg">
                Registra la tua azienda e inizia a noleggiare i tuoi veicoli attraverso la nostra piattaforma. 
                Raggiungi migliaia di potenziali clienti in tutta Italia.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "30 giorni di prova gratuita",
                  "Gestione completa della flotta",
                  "Dashboard dedicata per le prenotazioni",
                  "Supporto dedicato"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/register")}
                className="btn-primary"
                data-testid="cta-company-register"
              >
                Inizia la Prova Gratuita
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="md:col-span-5 mt-8 md:mt-0">
              <img
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Fleet management"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section for Companies */}
      <section className="py-20 bg-background" id="prezzi">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary uppercase tracking-widest text-sm font-bold">Piani e Prezzi</span>
            <h2 
              className="text-3xl md:text-4xl font-bold uppercase tracking-tight mt-2 mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Scegli il Piano <span className="text-primary">Perfetto per Te</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Inizia con 30 giorni di prova gratuita, poi scegli il piano più adatto alle tue esigenze.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div className="card-dashboard relative flex flex-col">
              <div className="mb-4">
                <div className="bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-bold text-xl mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Starter
                </h3>
                <p className="text-sm text-muted-foreground">
                  Perfetto per piccole attività
                </p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }}>€10</span>
                <span className="text-muted-foreground">/mese</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {[
                  "Fino a 5 veicoli",
                  "Supporto email",
                  "Dashboard base",
                  "Prenotazioni illimitate"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/register")}
                variant="outline"
                className="w-full"
              >
                Inizia la Prova
              </Button>
            </div>

            {/* Professional Plan - Recommended */}
            <div className="card-dashboard relative flex flex-col border-2 border-primary">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 uppercase tracking-wider">
                  Consigliato
                </span>
              </div>
              <div className="mb-4">
                <div className="bg-primary w-12 h-12 flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-primary-foreground" />
                </div>
                <h3 className="font-bold text-xl mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Professional
                </h3>
                <p className="text-sm text-muted-foreground">
                  Per aziende in crescita
                </p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }}>€20</span>
                <span className="text-muted-foreground">/mese</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {[
                  "Fino a 15 veicoli",
                  "Supporto prioritario",
                  "Statistiche avanzate",
                  "Badge verificato",
                  "Prenotazioni illimitate"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/register")}
                className="w-full btn-primary"
              >
                Inizia la Prova
              </Button>
            </div>

            {/* Enterprise Plan */}
            <div className="card-dashboard relative flex flex-col">
              <div className="mb-4">
                <div className="bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  <Crown className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-bold text-xl mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Enterprise
                </h3>
                <p className="text-sm text-muted-foreground">
                  Per grandi flotte
                </p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }}>€50</span>
                <span className="text-muted-foreground">/mese</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {[
                  "Veicoli illimitati",
                  "Supporto dedicato 24/7",
                  "API access",
                  "Account manager",
                  "Report personalizzati"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/register")}
                variant="outline"
                className="w-full"
              >
                Inizia la Prova
              </Button>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              Tutti i piani includono <strong>30 giorni di prova gratuita</strong>. Nessuna carta di credito richiesta.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Cosa Dicono i <span className="text-primary">Nostri Clienti</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "Marco R.",
                city: "Milano",
                text: "Servizio eccellente! Ho trovato il furgone perfetto per il mio trasloco in pochi minuti."
              },
              {
                name: "Laura B.",
                city: "Roma",
                text: "Noleggiamo regolarmente per le nostre consegne. Prezzi competitivi e veicoli sempre in ottime condizioni."
              },
              {
                name: "Giuseppe M.",
                city: "Napoli",
                text: "Finalmente una piattaforma seria per il noleggio furgoni. Assistenza sempre disponibile."
              }
            ].map((testimonial, index) => (
              <div key={index} className="card-dashboard">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-4">"{testimonial.text}"</p>
                <div>
                  <p className="font-medium">{testimonial.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {testimonial.city}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 
            className="text-2xl md:text-3xl font-bold text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Pronto a Noleggiare?
          </h2>
          <p className="text-white/80 mb-6 max-w-lg mx-auto">
            Inizia subito la tua ricerca e trova il furgone perfetto per le tue esigenze.
          </p>
          <Button 
            onClick={() => navigate("/catalogo")}
            className="bg-white text-primary hover:bg-white/90 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]"
            data-testid="final-cta-catalog"
          >
            Sfoglia il Catalogo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
